/**
 * Static stocker I/O load ports — always visible on the instanced shell,
 * matching how equipment load-port trays are drawn in the overview model.
 */
import React, { useMemo } from 'react';
import {
  StockerLoadPortBank,
  type LayoutStockerPortRef,
} from './amhs/Stocker/StockerLoadPortBank';
import { resolveKitBox, type EquipmentRenderDescriptor } from './equipmentSilhouettePorts';
import { EquipmentKitWorldGroup } from './EquipmentKitWorldGroup';
import type { LayoutElement } from '../../types';

type Props = {
  descriptor: EquipmentRenderDescriptor;
  bayCount?: string | number;
  outsideStocker?: boolean;
  layoutPorts?: LayoutStockerPortRef[];
};

export const StockerLoadPortShell3D: React.FC<Props> = React.memo(
  ({ descriptor, bayCount, outsideStocker = false, layoutPorts: layoutPortsProp }) => {
    const box = resolveKitBox(descriptor);
    const layoutPorts = useMemo(
      () => layoutPortsProp ?? [],
      [layoutPortsProp]
    );

    return (
      <EquipmentKitWorldGroup descriptor={descriptor}>
        <StockerLoadPortBank
          width={box.w}
          height={box.h}
          depth={box.d}
          bayCount={bayCount}
          outsideStocker={outsideStocker}
          layoutPorts={layoutPorts}
        />
      </EquipmentKitWorldGroup>
    );
  }
);

StockerLoadPortShell3D.displayName = 'StockerLoadPortShell3D';

export function resolveStockerShellLayoutPorts(
  elementId: string,
  layoutMeta?: Record<string, Pick<LayoutElement, 'customProperties'>>
): LayoutStockerPortRef[] {
  const el = layoutMeta?.[elementId];
  const ports = el?.customProperties?.ports;
  return Array.isArray(ports) ? (ports as LayoutStockerPortRef[]) : [];
}

export function resolveStockerShellMeta(
  elementId: string,
  layoutMeta?: Record<string, Pick<LayoutElement, 'customProperties'>>
): { bayCount?: string | number; outsideStocker: boolean } {
  const el = layoutMeta?.[elementId];
  const cp = el?.customProperties;
  const bayCount = cp?.bayCount ?? cp?.stockerBayCount;
  return {
    bayCount: typeof bayCount === 'string' || typeof bayCount === 'number' ? bayCount : undefined,
    outsideStocker: Boolean(cp?.outsideStocker),
  };
}

export default StockerLoadPortShell3D;
